import {
    FetchDocCategoriesByIdsArgs,
    IDocCategorySelectOption,
} from 'components/DocCategorySelect/types';
import { INTERNAL_DOC_CATEGORIES_PER_PAGE } from 'components/InternalDocCategoriesConfig/constants';
import { DocumentCategory } from 'gql-types';
import { queryClient } from 'lib/queries';
import {
    CACHE_TIME_MS,
    GET_DOC_CATEGORIES_BY_IDS_QUERY,
} from 'lib/queriesConstants';
import { getDocCategories } from 'services/documentCategories/api';
import logger from 'services/logger';

export const getDocCategoryForSelectOption = (
    docCategory: DocumentCategory,
) => ({
    label: docCategory.title,
    value: docCategory.id,
    companyId: docCategory.companyId,
});

export const getDocCategoriesForSelectOptions = (
    docCategories: DocumentCategory[],
): IDocCategorySelectOption[] =>
    docCategories.map(getDocCategoryForSelectOption);

export const getSelectOptionSet = (options: IDocCategorySelectOption[]) =>
    Array.from(new Set(options.map((a) => JSON.stringify(a)))).map((str) =>
        JSON.parse(str),
    );

export const getIsOptionsHasDefaultValue = (
    options: IDocCategorySelectOption[],
    defaultValue?: string | string[],
) => {
    if (typeof defaultValue === 'string') {
        return options.some((option) => String(option.value) === defaultValue);
    }
    // for multi select
    if (Array.isArray(defaultValue)) {
        const filteredOptions = options.filter((option) =>
            defaultValue.includes(String(option.value)),
        );

        return filteredOptions.length === defaultValue.length;
    }

    return false;
};

export const getIdsArgForSending = (ids?: FetchDocCategoriesByIdsArgs['ids']) =>
    [ids || []]
        .flat()
        .map(Number)
        // In some cases we can get "undefined" string in "ids" array.
        // It should be fixed in upstream code, but for now we need to filter it out.
        .filter((id) => !isNaN(id));

export const fetchDocCategoriesByIds = async ({
    title = '',
    limit = INTERNAL_DOC_CATEGORIES_PER_PAGE,
    offset = 0,
    onlyInternal = false,
    onlyPublic = false,
    ids,
}: FetchDocCategoriesByIdsArgs) => {
    const args = {
        title,
        offset,
        limit,
        onlyInternal,
        onlyPublic,
        ids: getIdsArgForSending(ids),
    };

    try {
        return await queryClient.fetchQuery({
            queryKey: [GET_DOC_CATEGORIES_BY_IDS_QUERY, args],
            queryFn: async () => await getDocCategories(args),
            staleTime: CACHE_TIME_MS,
        });
    } catch (error) {
        logger.error('Failed to fetch doc categories by ids', {
            error,
        });
        return [];
    }
};
